import { fetch_questions_in_project } from '@/services/question_service';
import {
  buildEditableMainSectionState,
  buildEditableSectionState,
  buildSubSectionModalState,
  findProjectQuestion,
  mapMainSectionsWithQuestions,
  mapSectionsDeletingState,
  resolveProjectId,
  resolveQuestionModalState,
  resolveSectionRef,
  updateQuestionUnassigningState,
} from './create_sections_modal_helpers.js';
import {
  hasProjectQuestionContext,
  mapQuestionsWithUnassigningState,
  mapSectionsWithExpandedState,
  normalizeProjectId,
  resetCurrentEditQuestion,
  resolveEditableSection,
  resolveMainSectionId,
  resolveSectionId,
  syncCurrentEditMainSection,
  syncMainSectionDeletingState,
  updateSectionDeletingState,
} from './create_sections_modal_state_helpers.js';

export const create_sections_modal_methods = {
  open_main_section_modal() {
    this.main_section_name_error = false;
    this.order_number_error = false;
    this.main_section_mode = 'new';
    this.new_main_section = { main_section_name: '', order_number: this.main_sections.length + 1, existing_main_section_id: '' };
    this.main_section_modal_open = true;
  },

  open_sub_section_modal(main_section_id = null) {
    this.sub_section_name_error = false;
    this.order_number_error = false;
    this.sub_section_mode = 'new';
    const modal_state = buildSubSectionModalState(this, main_section_id);
    this.active_main_section_id = modal_state.active_main_section_id;
    this.active_main_section_name = modal_state.active_main_section_name;
    this.new_sub_section = {
      section_name: '',
      order_number: Array.isArray(modal_state.active_main_section?.sections)
        ? modal_state.active_main_section.sections.length + 1
        : 1,
      existing_section_id: '',
    };
    this.subsection_modal_open = true;
  },

  open_edit_section_modal(section_index) {
    const editable_section = resolveEditableSection(this, section_index, resolveSectionRef);
    if (!editable_section) {
      this.show_error('Please select a valid sub section first.');
      return;
    }
    this.edit_section_name_error = false;
    this.current_edit_section_index = section_index;
    this.current_edit_section = buildEditableSectionState(
      editable_section.section_id,
      editable_section.section
    );
  },

  open_edit_main_section_modal(main_section) {
    const main_section_id = resolveMainSectionId(main_section);
    if (!main_section || !main_section_id) {
      this.show_error('Please select a valid main section first.');
      return;
    }
    this.edit_main_section_name_error = false;
    this.current_edit_main_section = buildEditableMainSectionState(main_section_id, main_section);
  },

  async open_question_modal(sub_section) {
  const modal_state = resolveQuestionModalState(this, sub_section);

  this.active_question_section = modal_state.section;
  this.active_question_section_index = modal_state.section_index;
  this.question_text_error = false;
  this.question_forms = modal_state.question_forms;
  await this.$nextTick();
},
  close_question_modal() {
    this.question_modal_open = false;
    this.question_text_error = false;
    this.active_question_section = null;
    this.active_question_section_index = null;
    this.question_forms = [];
  },
  close_edit_section_modal() {
    this.edit_section_name_error = false;
    this.current_edit_section_index = null;
    this.current_edit_section = { section_id: null, name: '', is_deleting: false };
  },

  close_edit_main_section_modal() {
    this.edit_main_section_name_error = false;
    this.current_edit_main_section = { main_section_id: null, main_section_name: '', main_section_description: '', is_deleting: false };
  },

  close_sub_section_modal() {
    this.subsection_modal_open = false;
    this.sub_section_name_error = false;
    this.order_number_error = false;
    this.sub_section_mode = 'new';
    this.active_main_section_name = '';
    this.active_main_section_id = null;
    this.new_sub_section = { section_name: '', order_number: 0, existing_section_id: '' };
  },

  close_edit_question_modal() {
    this.edit_question_modal_open = false;
    this.edit_question_error = false;
    this.active_edit_section_index = null;
    this.active_question_index = null;
    resetCurrentEditQuestion(this);
  },

  expand_section_by_id(section_id) {
    const target_section_id = Number(section_id);
    if (!target_section_id) return;
    this.main_sections = (Array.isArray(this.main_sections) ? this.main_sections : []).map((main_section) => {
      return { ...main_section, sections: mapSectionsWithExpandedState(main_section.sections, target_section_id, true) };
    });
    this.sections = mapSectionsWithExpandedState(this.sections, target_section_id, true);
  },

  set_question_unassigning(section_id, question_id, is_unassigning) {
    const normalized_section_id = Number(section_id);
    const normalized_question_id = Number(question_id);
    if (!normalized_section_id || !normalized_question_id) return;
    this.main_sections = mapMainSectionsWithQuestions(
      this.main_sections,
      normalized_section_id,
      normalized_question_id,
      is_unassigning,
      resolveSectionId,
      (questions, target_question_id, next_is_unassigning) => {
        return mapQuestionsWithUnassigningState(
          questions,
          target_question_id,
          next_is_unassigning,
          updateQuestionUnassigningState
        );
      }
    );
    this.sections = (Array.isArray(this.sections) ? this.sections : []).map((section) => {
      if (resolveSectionId(section) !== normalized_section_id) return section;
      return {
        ...section,
        questions: mapQuestionsWithUnassigningState(
          section.questions,
          normalized_question_id,
          is_unassigning,
          updateQuestionUnassigningState
        ),
      };
    });
  },

  expand_main_section_by_id(main_section_id) {
    const target_main_section_id = Number(main_section_id);
    if (!target_main_section_id) return;
    this.main_sections = (Array.isArray(this.main_sections) ? this.main_sections : []).map((main_section) => {
      if (resolveMainSectionId(main_section) !== target_main_section_id) return main_section;
      return {
        ...main_section,
        sections: (Array.isArray(main_section.sections) ? main_section.sections : []).map((section) => {
          const expanded_section = { ...section, expanded: true };
          return expanded_section;
        }),
      };
    });
  },

  toggle_section(section) {
    const target_section_id = resolveSectionId(section);
    if (!target_section_id) return;
    const next_expanded_state = !section?.expanded;
    this.main_sections = (Array.isArray(this.main_sections) ? this.main_sections : []).map((main_section) => {
      return { ...main_section, sections: mapSectionsWithExpandedState(main_section.sections, target_section_id, next_expanded_state) };
    });
    this.sections = mapSectionsWithExpandedState(this.sections, target_section_id, next_expanded_state);
  },

  set_sub_section_deleting(section_id, is_deleting) {
    const normalized_section_id = Number(section_id);
    if (!normalized_section_id) return;
    const next_state = mapSectionsDeletingState(
      this.main_sections,
      this.sections,
      normalized_section_id,
      is_deleting,
      updateSectionDeletingState
    );
    this.main_sections = next_state.main_sections;
    this.sections = next_state.sections;
    if (Number(this.current_edit_section?.section_id || 0) === normalized_section_id) {
      this.current_edit_section = { ...this.current_edit_section, is_deleting: is_deleting };
    }
  },

  set_main_section_deleting(main_section_id, is_deleting) {
    const normalized_main_section_id = Number(main_section_id);
    if (!normalized_main_section_id) return;
    syncMainSectionDeletingState(this, normalized_main_section_id, is_deleting);
    syncCurrentEditMainSection(this, normalized_main_section_id, is_deleting);
  },

  async fetch_project_id_for_question(question_id, form_id) {
    const normalized_question_id = Number(question_id);
    const normalized_form_id = Number(form_id);
    if (!hasProjectQuestionContext(normalized_question_id, normalized_form_id)) return '';
    try {
      const rows = await fetch_questions_in_project({ form_id: normalized_form_id });
      const matched_question = findProjectQuestion(rows, normalized_question_id);
      if (!matched_question) return '';
      const project_id = resolveProjectId(matched_question);
      const normalized_project_id = normalizeProjectId(project_id);
      return normalized_project_id;
    } catch {
      return '';
    }
  },

  set_page(page) {
    if (page < 1 || page > this.total_pages) {
      return;
    }
    this.current_page = page;
  },

  reset_pagination() {
    this.current_page = 1;
  },
};
